import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getErrorMessage } from '../../utils/errorHelpers';
import { Check, X, Key, Loader2, Trash2, TestTube, Star } from 'lucide-react';

const TenantAIKeyManagement = ({ tenantId, tenantName, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [testing, setTesting] = useState({});
  const [testResults, setTestResults] = useState({});

  // Multi-provider state
  const [providerKeys, setProviderKeys] = useState({
    gemini: { configured: false, key: '' },
    openai: { configured: false, key: '' },
    claude: { configured: false, key: '' }
  });
  const [defaultProvider, setDefaultProvider] = useState('gemini');
  const [keyStrategy, setKeyStrategy] = useState('platform_shared');
  const [fallbackToPlatform, setFallbackToPlatform] = useState(true);

  const providers = [
    { id: 'gemini', name: 'Google Gemini', color: 'blue' },
    { id: 'openai', name: 'OpenAI', color: 'green' },
    { id: 'claude', name: 'Anthropic Claude', color: 'purple' }
  ];

  useEffect(() => {
    fetchConfig();
  }, [tenantId]);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/tenants/${tenantId}/ai-keys`
      );
      
      const data = response.data;
      setKeyStrategy(data.key_strategy || 'platform_shared');
      setDefaultProvider(data.default_provider || 'gemini');
      setFallbackToPlatform(data.fallback_to_platform !== false);
      
      // Update provider keys state
      const newProviderKeys = { ...providerKeys };
      Object.keys(data.provider_keys || {}).forEach(provider => {
        if (newProviderKeys[provider]) {
          newProviderKeys[provider] = {
            ...newProviderKeys[provider],
            ...data.provider_keys[provider],
            key: '' // Don't show actual key
          };
        }
      });
      setProviderKeys(newProviderKeys);
      
    } catch (error) {
      console.error('Error fetching config:', error);
      // Try legacy endpoint
      try {
        const legacyResponse = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/admin/tenants/${tenantId}/ai-config`
        );
        const legacyData = legacyResponse.data;
        setKeyStrategy(legacyData.key_strategy || 'platform_shared');
        if (legacyData.ai_provider && legacyData.has_key) {
          setProviderKeys(prev => ({
            ...prev,
            [legacyData.ai_provider]: {
              configured: true,
              status: legacyData.key_status,
              key: ''
            }
          }));
        }
      } catch (e) {
        console.error('Legacy fetch also failed:', e);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveKey = async (provider) => {
    const key = providerKeys[provider]?.key;
    if (!key || key.length < 10) {
      alert('Please enter a valid API key');
      return;
    }

    setSaving(prev => ({ ...prev, [provider]: true }));
    setTestResults(prev => ({ ...prev, [provider]: null }));

    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/tenants/${tenantId}/ai-keys/${provider}`,
        null,
        {
          params: {
            api_key: key,
            set_as_default: !Object.values(providerKeys).some(p => p.configured)
          }
        }
      );

      alert(`${provider.toUpperCase()} key saved successfully!`);
      setProviderKeys(prev => ({
        ...prev,
        [provider]: { ...prev[provider], key: '', configured: true, status: 'active' }
      }));
      
      // Update strategy if this is first key
      if (keyStrategy === 'platform_shared') {
        setKeyStrategy('tenant_dedicated');
      }
      
    } catch (error) {
      console.error('Error saving key:', error);
      const errorMsg = getErrorMessage(error, 'Failed to save');
      alert(`Failed to save: ${errorMsg}`);
    } finally {
      setSaving(prev => ({ ...prev, [provider]: false }));
    }
  };

  const handleDeleteKey = async (provider) => {
    if (!confirm(`Are you sure you want to delete the ${provider.toUpperCase()} key?`)) {
      return;
    }

    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/tenants/${tenantId}/ai-keys/${provider}`
      );

      alert(`${provider.toUpperCase()} key deleted`);
      setProviderKeys(prev => ({
        ...prev,
        [provider]: { configured: false, key: '', status: null }
      }));
      setTestResults(prev => ({ ...prev, [provider]: null }));
      
    } catch (error) {
      console.error('Error deleting key:', error);
      alert('Failed to delete key');
    }
  };

  const handleTestKey = async (provider) => {
    setTesting(prev => ({ ...prev, [provider]: true }));
    setTestResults(prev => ({ ...prev, [provider]: null }));

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/tenants/${tenantId}/ai-keys/${provider}/test`
      );

      setTestResults(prev => ({ ...prev, [provider]: response.data }));
      
      if (response.data.success) {
        setProviderKeys(prev => ({
          ...prev,
          [provider]: { ...prev[provider], status: 'active' }
        }));
      }
      
    } catch (error) {
      console.error('Error testing key:', error);
      setTestResults(prev => ({
        ...prev,
        [provider]: { success: false, message: getErrorMessage(error, 'Test failed') }
      }));
    } finally {
      setTesting(prev => ({ ...prev, [provider]: false }));
    }
  };

  const handleSetDefault = async (provider) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/tenants/${tenantId}/ai-keys/default`,
        null,
        { params: { provider } }
      );

      setDefaultProvider(provider);
      alert(`${provider.toUpperCase()} set as default provider`);
      
    } catch (error) {
      console.error('Error setting default:', error);
      alert('Failed to set default provider');
    }
  };

  const handleKeyInputChange = (provider, value) => {
    setProviderKeys(prev => ({
      ...prev,
      [provider]: { ...prev[provider], key: value }
    }));
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  const configuredCount = Object.values(providerKeys).filter(p => p.configured).length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold">AI Key Management</h2>
            <p className="text-gray-600 text-sm">{tenantName}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Strategy Selection */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold mb-3">Key Strategy</h3>
            <div className="flex gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="strategy"
                  checked={keyStrategy === 'platform_shared'}
                  onChange={() => setKeyStrategy('platform_shared')}
                  className="mr-2"
                />
                <span>Platform Shared (Emergent Key)</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="strategy"
                  checked={keyStrategy === 'tenant_dedicated'}
                  onChange={() => setKeyStrategy('tenant_dedicated')}
                  className="mr-2"
                />
                <span>Tenant Dedicated Keys</span>
              </label>
            </div>
            {keyStrategy === 'tenant_dedicated' && (
              <p className="text-sm text-gray-600 mt-2">
                Configure one or more provider keys below. You have {configuredCount}/3 providers configured.
              </p>
            )}
          </div>

          {/* Provider Keys */}
          {keyStrategy === 'tenant_dedicated' && (
            <div className="space-y-4">
              <h3 className="font-semibold">Provider API Keys</h3>
              
              {providers.map(provider => {
                const keyData = providerKeys[provider.id];
                const isDefault = defaultProvider === provider.id;
                const testResult = testResults[provider.id];
                
                return (
                  <div
                    key={provider.id}
                    className={`border rounded-lg p-4 ${
                      keyData?.configured ? 'border-green-300 bg-green-50' : 'border-gray-200'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-2">
                        <Key className={`w-5 h-5 text-${provider.color}-600`} />
                        <span className="font-medium">{provider.name}</span>
                        {isDefault && keyData?.configured && (
                          <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded text-xs flex items-center gap-1">
                            <Star className="w-3 h-3" /> Default
                          </span>
                        )}
                        {keyData?.configured && (
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            keyData.status === 'active' ? 'bg-green-100 text-green-800' :
                            keyData.status === 'failed' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {keyData.status || 'Unknown'}
                          </span>
                        )}
                      </div>
                      
                      {keyData?.configured && (
                        <div className="flex gap-2">
                          {!isDefault && (
                            <button
                              onClick={() => handleSetDefault(provider.id)}
                              className="text-xs px-2 py-1 bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200"
                              title="Set as default"
                            >
                              Set Default
                            </button>
                          )}
                          <button
                            onClick={() => handleTestKey(provider.id)}
                            disabled={testing[provider.id]}
                            className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 flex items-center gap-1"
                          >
                            {testing[provider.id] ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <TestTube className="w-3 h-3" />
                            )}
                            Test
                          </button>
                          <button
                            onClick={() => handleDeleteKey(provider.id)}
                            className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Test Result */}
                    {testResult && (
                      <div className={`mb-3 p-2 rounded text-sm flex items-center gap-2 ${
                        testResult.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {testResult.success ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                        {testResult.message}
                      </div>
                    )}

                    {/* Key Input */}
                    <div className="flex gap-2">
                      <input
                        type="password"
                        placeholder={keyData?.configured ? "Enter new key to replace" : `Enter ${provider.name} API key`}
                        value={keyData?.key || ''}
                        onChange={(e) => handleKeyInputChange(provider.id, e.target.value)}
                        className="flex-1 px-3 py-2 border rounded-lg text-sm"
                      />
                      <button
                        onClick={() => handleSaveKey(provider.id)}
                        disabled={saving[provider.id] || !keyData?.key}
                        className={`px-4 py-2 rounded-lg text-white text-sm flex items-center gap-2 ${
                          saving[provider.id] || !keyData?.key
                            ? 'bg-gray-400 cursor-not-allowed'
                            : `bg-${provider.color}-600 hover:bg-${provider.color}-700`
                        }`}
                        style={{
                          backgroundColor: saving[provider.id] || !keyData?.key ? undefined :
                            provider.color === 'blue' ? '#2563eb' :
                            provider.color === 'green' ? '#16a34a' :
                            '#9333ea'
                        }}
                      >
                        {saving[provider.id] ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          'Save'
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Fallback Option */}
          {keyStrategy === 'tenant_dedicated' && (
            <div className="bg-gray-50 rounded-lg p-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={fallbackToPlatform}
                  onChange={(e) => setFallbackToPlatform(e.target.checked)}
                  className="mr-2"
                />
                <span className="text-sm">
                  Fallback to platform key if tenant key fails
                </span>
              </label>
            </div>
          )}

          {/* Summary */}
          <div className="bg-blue-50 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 mb-2">Current Configuration</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Strategy: <strong>{keyStrategy === 'platform_shared' ? 'Platform Shared' : 'Tenant Dedicated'}</strong></li>
              {keyStrategy === 'tenant_dedicated' && (
                <>
                  <li>• Default Provider: <strong>{defaultProvider?.toUpperCase()}</strong></li>
                  <li>• Configured Keys: <strong>{configuredCount}</strong> ({
                    providers.filter(p => providerKeys[p.id]?.configured).map(p => p.name).join(', ') || 'None'
                  })</li>
                  <li>• Fallback Enabled: <strong>{fallbackToPlatform ? 'Yes' : 'No'}</strong></li>
                </>
              )}
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TenantAIKeyManagement;
